import { useRef, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@mlm/ui/lib/cn';
import { CoachButton } from './primitives';

/**
 * The one destructive-confirm pattern in the coach app. It replaced a button
 * that swapped itself for "Remove it / Keep" in the same spot, which a tester
 * read as no confirmation at all: nothing moved except the words under their
 * thumb. A sheet on the phone and a centred panel on desktop are unmistakably
 * a question being asked.
 *
 * Confirm is `outline`, never gold — gold stays the one primary decision per
 * screen (SUCCESS_COACH_DESIGN_SYSTEM.md), and deleting is not that decision.
 * While `busy`, Escape and an outside tap are ignored so a request in flight
 * cannot be abandoned half-way.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Keep',
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const keepRef = useRef<HTMLButtonElement>(null);
  const keepBusy = (e: Event) => {
    if (busy) e.preventDefault();
  };
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-canvas/80" />
        <DialogPrimitive.Content
          onEscapeKeyDown={keepBusy}
          onPointerDownOutside={keepBusy}
          onInteractOutside={keepBusy}
          onOpenAutoFocus={(e) => {
            // The safe choice takes focus, not the destructive one.
            e.preventDefault();
            keepRef.current?.focus();
          }}
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 border-t border-border bg-canvas px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6',
            'lg:inset-auto lg:left-1/2 lg:top-1/2 lg:w-full lg:max-w-md lg:-translate-x-1/2 lg:-translate-y-1/2 lg:border lg:p-8',
          )}
        >
          <DialogPrimitive.Title className="font-display text-[1.5rem] font-semibold leading-[1.2] text-ink">
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-2 text-[0.93rem] leading-relaxed text-ink/70">
            {body ?? 'This cannot be undone.'}
          </DialogPrimitive.Description>
          <div className="mt-6 flex items-center gap-5">
            <CoachButton kind="outline" disabled={busy} onClick={onConfirm}>
              {busy ? 'Working' : confirmLabel}
            </CoachButton>
            <CoachButton ref={keepRef} kind="quiet" disabled={busy} onClick={onCancel}>
              {cancelLabel}
            </CoachButton>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
